import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
const StudentDashboardInfo = React.lazy(() => import("./StudentDashboardInfo"));

const StudentDashboard: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "dashboard" | "resources" | "tests" | "homework"
  >("dashboard");
  const location = useLocation();
  const navigate = useNavigate();

  // Sync tab from query param (?tab=resources/tests/dashboard/homework)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "resources" || tab === "tests" || tab === "dashboard" || tab === "homework") {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Lazy load student sections
  const StudentResources = React.lazy(() => import("./StudentResources"));
  const StudentHomework = React.lazy(() => import("./StudentHomework"));

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar toggle button (persistent, floating) */}
      <button
        className="fixed bottom-4 left-4 z-50 bg-blue-800 text-white p-3 rounded-full shadow-lg focus:outline-none md:hidden"
        aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
        onClick={() => setSidebarOpen((open) => !open)}
      >
        {sidebarOpen ? (
          // X icon
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          // Hamburger icon
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        )}
      </button>
      <div className="flex h-screen">
        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}
        <div
          className={`w-64 bg-slate-900 text-white p-4 flex flex-col z-40 fixed md:static h-full top-0 left-0 transition-transform duration-300 md:translate-x-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          }`}
          style={{ minHeight: "100vh" }}
        >
          {/* Close button on mobile */}
          <div className="flex md:hidden justify-end mb-2">
            <button
              className="text-white p-2 rounded-full hover:bg-slate-800 focus:outline-none"
              aria-label="Close sidebar"
              onClick={() => setSidebarOpen(false)}
            >
              {/* X icon */}
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <h2 className="text-2xl font-bold text-center mt-2 mb-2">
            Student Panel
          </h2>
          <hr className="w-full border-t border-slate-200/30 mb-4" />
          <nav>
            <ul className="space-y-2">
              <li>
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold w-full text-left ${
                    activeTab === "dashboard"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40 hover:bg-blue-600/80 focus:bg-blue-700/90"
                  }`}
                  onClick={() => {
                    setActiveTab("dashboard");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Home */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 12l2-2m0 0l7-7 7 7m-9 2v8m4-8v8m5 0a2 2 0 01-2 2H7a2 2 0 01-2-2v-7a2 2 0 012-2h10a2 2 0 012 2v7z"
                    />
                  </svg>
                  Dashboard
                </button>
              </li>
              <li>
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold w-full text-left ${
                    activeTab === "resources"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40 hover:bg-blue-600/80 focus:bg-blue-700/90"
                  }`}
                  onClick={() => {
                    setActiveTab("resources");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Collection */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 11H5m14 4H5m7-8v12"
                    />
                  </svg>
                  Resources
                </button>
              </li>
              <li>
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold w-full text-left ${
                    activeTab === "tests"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40 hover:bg-blue-600/80 focus:bg-blue-700/90"
                  }`}
                  onClick={() => {
                    setActiveTab("tests");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Clipboard List */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5h6M9 3h6a2 2 0 012 2v14a2 2 0 01-2 2H9a2 2 0 01-2-2V5a2 2 0 012-2z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 9h6M9 13h6M9 17h6"
                    />
                  </svg>
                  Tests
                </button>
              </li>
              <li>
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold w-full text-left ${
                    activeTab === "homework"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40 hover:bg-blue-600/80 focus:bg-blue-700/90"
                  }`}
                  onClick={() => {
                    setActiveTab("homework");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Clipboard with Check */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7 7h10a2 2 0 012 2v9a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
                  </svg>
                  Homework
                </button>
              </li>
            </ul>
            <hr className="mt-16 w-full border-t border-slate-200/30 mb-4" />
            <button onClick={handleLogout} className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-semibold shadow flex items-center justify-center gap-2">
              {/* Icon: Logout */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1"
                />
              </svg>
              Logout
            </button>
          </nav>
        </div>
        {/* Main Content */}
        <div
          className="flex-1 p-2 overflow-y-auto"
          onClick={() => sidebarOpen && setSidebarOpen(false)}
        >
          <React.Suspense
            fallback={<div className="text-center py-8">Loading...</div>}
          >
            {activeTab === "dashboard" && (
              <React.Suspense
                fallback={<div className="text-center py-8">Loading...</div>}
              >
                <StudentDashboardInfo />
              </React.Suspense>
            )}
            {activeTab === "resources" && <StudentResources />}
            {activeTab === "homework" && <StudentHomework />}
            {activeTab === "tests" && (
              <React.Suspense fallback={<div className="text-center py-8">Loading...</div>}>
                {React.createElement(require("./StudentTests").default)}
              </React.Suspense>
            )}
          </React.Suspense>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
